import React from 'react';

/**
 * Shared sizing for the company / location / model pickers.
 *
 * PrimeReact sizes a dropdown panel to its widest option, so one long company name stretches the
 * panel past the dialog it sits in. Capping the panel and wrapping the option text keeps the name
 * readable in full without the panel deciding the layout.
 */
export const PICKER_PANEL_CLASS = 'max-w-[32rem]';

/** Option row: wraps onto as many lines as the name needs rather than widening the panel. */
export const wrappingItemTemplate = (option: { label: string } | null): React.ReactNode =>
  option ? React.createElement('span', { className: 'block whitespace-normal break-words' }, option.label) : null;

/**
 * Virtual-scroller wiring for a lazily paged picker: the next page is asked for as the list is
 * scrolled to the end of what it currently holds, rather than everything being fetched up front.
 */
export const lazyScrollerOptions = (loading: boolean, loadedCount: number, hasMore: boolean, loadMore: () => void) => ({
  lazy: true,
  itemSize: ITEM_SIZE,
  showLoader: true,
  loading,
  onLazyLoad: (event: { first: number; last: number }) => {
    // PrimeReact reports the visible window; the tail of it reaching the loaded count is the signal.
    if (hasMore && event.last >= loadedCount - 1) loadMore();
  },
});

/** Row height the virtual scroller assumes. Matches the option padding below. */
const ITEM_SIZE = 38;

/**
 * The closed trigger, where wrapping is not an option — the control is one row high, so a long name
 * is truncated with an ellipsis and shown in full via `title`.
 */
export const truncatedValueTemplate = (placeholder: string) => (option: { label: string } | null): React.ReactNode =>
  option
    ? React.createElement('span', { className: 'block truncate', title: option.label }, option.label)
    : React.createElement('span', { className: 'text-content-tertiary' }, placeholder);
